/**
 * invitationTelemetry — one registry for invitation-flow telemetry event names.
 *
 * Covers two halves of the flow:
 * - ACCEPT: a user opening an invitation link and consuming it
 *   (`/invitation/:token` and `/demo/invitation/:invitationId`).
 * - CREATE / COPY: an instructor or admin generating an invitation and copying
 *   its link (section, TA/assistant, catalog, organization, demo).
 *
 * Emit a constant from here rather than a string literal so there is one list
 * to audit and the GA/BigQuery names stay consistent. Add a constant here
 * before you emit it.
 *
 * All events go through `repository.logEvent(name, params)`, which already
 * stamps `tab_id` and `tab_age_ms` on every row. The params documented in the
 * comments below are what each call site adds on top.
 */
export const InvitationTelemetryEvent = {
  // --- Accept flow: /invitation/:token ---
  /** The token accept page mounted and has a token to process. */
  acceptPageViewed: 'invitation_accept_page_viewed',
  /** The page opened without a token; we redirect home. */
  acceptNoToken: 'invitation_accept_no_token',
  /** An admin (Corre) opened a non-section invite; we deny and redirect home. */
  acceptAdminDenied: 'invitation_accept_admin_denied',
  /** `checkIsValidInvitation` returned. Params: is_valid, invitation_type?, invalid_reason?. */
  checkValidCompleted: 'invitation_check_valid_completed',
  /** A valid non-section invite was accepted without prompting. Params: invitation_type. */
  verifyAutoAccepted: 'invitation_verify_auto_accepted',
  /** The confirm/verify dialog was shown. Params: is_valid, invitation_type?. */
  verifyPromptShown: 'invitation_verify_prompt_shown',
  /** The user clicked accept in the verify dialog. */
  verifyAccepted: 'invitation_verify_accepted',
  /** The user clicked reject in the verify dialog. */
  verifyRejected: 'invitation_verify_rejected',
  /** The user clicked the logout link in the verify dialog. */
  verifyLogout: 'invitation_verify_logout',
  /**
   * A consume decision was reached (any outcome). Note this also fires for
   * pre-consume handled errors from validation (e.g. expired, not_found,
   * already-consumed) where `consumeInvitation` was never called, so it is not
   * a 1:1 count of consume RPCs. Params: result (enum name).
   */
  consumeCompleted: 'invitation_consume_completed',
  /**
   * The invitation was accepted successfully. Emitted once, from
   * `BreakoutUser.consumeInvitation` (covers both the token and demo pages).
   * Params: invitation_id, promotion_id?, section_id?, slide_deck_review_id?.
   */
  accepted: 'invitation_accepted',
  /** Redirected to the slide-deck review after a review invite. Params: slide_deck_id. */
  navigateSlideDeckReview: 'invitation_navigate_slide_deck_review',
  /** The post-accept "you joined" section dialog was shown. Params: section_id. */
  acceptedDialogShown: 'invitation_accepted_dialog_shown',

  // --- Accept flow: /demo/invitation/:invitationId ---
  /** The demo accept page mounted. */
  demoAcceptPageViewed: 'invitation_demo_accept_page_viewed',
  /** Anonymous sign-in for the demo failed. Params: referrer_blocked. */
  demoAnonymousSignInFailed: 'invitation_demo_anonymous_signin_failed',
  /** A demo consume attempt finished (any outcome). Params: result (enum name). */
  demoConsumeCompleted: 'invitation_demo_consume_completed',
  /** The demo invitation was accepted successfully. */
  demoAccepted: 'invitation_demo_accepted',
  /** Waiting for the demo room failed after accept. Params: canceled. */
  demoRoomWaitFailed: 'invitation_demo_room_wait_failed',
  /**
   * The demo consume + post-consume navigation chain rejected. Params:
   * error_name?, error_message? (so consume vs navigation failures are
   * distinguishable).
   */
  demoConsumeError: 'invitation_demo_consume_error',

  // --- Create / copy: section (student) invites ---
  /** The multi-use student invite link was generated. Params: section_id. */
  sectionLinkGenerated: 'invitation_section_link_generated',
  /** Generating the student invite link failed. Params: section_id. */
  sectionLinkGenerateFailed: 'invitation_section_link_generate_failed',
  /** The student invite link or message was copied. Params: section_id, full_message. */
  sectionLinkCopied: 'invitation_section_link_copied',
  /** Copying the student invite link or message failed. Params: section_id, full_message. */
  sectionLinkCopyFailed: 'invitation_section_link_copy_failed',

  // --- Create / copy: TA / assistant invites ---
  /** Assistant invite creation started. Params: access_level, section_count. */
  assistantCreateAttempted: 'invitation_assistant_create_attempted',
  /** Assistant invite created. Params: access_level, section_count. */
  assistantCreated: 'invitation_assistant_created',
  /** Assistant invite creation failed. Params: access_level. */
  assistantCreateFailed: 'invitation_assistant_create_failed',
  /** Assistant invite update started. Params: access_level, section_count. */
  assistantUpdateAttempted: 'invitation_assistant_update_attempted',
  /** Assistant invite updated. Params: access_level, section_count. */
  assistantUpdated: 'invitation_assistant_updated',
  /** Assistant invite update failed. Params: access_level. */
  assistantUpdateFailed: 'invitation_assistant_update_failed',
  /** An assistant invite link was copied (dialog or list row). */
  assistantLinkCopied: 'invitation_assistant_link_copied',
  /** Copying an assistant invite link failed. */
  assistantLinkCopyFailed: 'invitation_assistant_link_copy_failed',
  /** The instructor confirmed removing a pending assistant invite. */
  assistantRemoveConfirmed: 'invitation_assistant_remove_confirmed',

  // --- Create / copy: admin catalog + instructor invites ---
  /**
   * An admin generated an instructor+catalog invite. Params: catalog_count,
   * success (the clipboard write; creation itself succeeded).
   */
  adminInstructorCreated: 'invitation_admin_instructor_created',
  /** Creating an instructor+catalog invite failed. Params: catalog_count. */
  adminInstructorCreateFailed: 'invitation_admin_instructor_failed',
  /**
   * An admin generated a catalog invite. Params: invitation_type, success (the
   * clipboard write; creation itself succeeded).
   */
  catalogCreated: 'invitation_catalog_created',
  /** Creating a catalog invite failed. Params: invitation_type. */
  catalogCreateFailed: 'invitation_catalog_create_failed',

  // --- Create / copy: organization admin invites ---
  /** Org admin invite creation started. */
  orgAdminCreateAttempted: 'invitation_org_admin_create_attempted',
  /** Org admin invite created. */
  orgAdminCreated: 'invitation_org_admin_created',
  /** Org admin invite creation failed. */
  orgAdminCreateFailed: 'invitation_org_admin_create_failed',

  // --- Create / copy: demo invites ---
  /** A demo meeting's invitations were created. Params: invitee_count. */
  demoCreated: 'invitation_demo_created',
} as const

export type InvitationTelemetryEventName =
  (typeof InvitationTelemetryEvent)[keyof typeof InvitationTelemetryEvent]
